import { Component, effect, ElementRef, OnDestroy, OnInit, inject, input, output, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule, DatePipe } from '@angular/common';
import {
  ModalController,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonCheckbox,
  IonSpinner,
  IonList,
  IonItem,
  IonLabel,
  IonChip,
  IonSelect,
  IonSelectOption,
  IonPopover,
  IonBadge,
} from '@ionic/angular/standalone';
import { AuthService } from '../../../auth/auth.service';
import { environment } from '../../../../environments/environment';
import { AdvanceType } from '../../../../models/db-models';
import { PayeeService } from 'src/app/services/payee.service';
import { InstantSearchService } from '../../../services/algolia/instant-search.service';
import { BaseHit } from 'instantsearch.js';
import {
  connectHits,
  connectPagination,
  connectSearchBox,
  connectSortBy,
} from 'instantsearch.js/es/connectors';
import { pagination, searchBox, stats } from 'instantsearch.js/es/widgets';
import configure from 'instantsearch.js/es/widgets/configure/configure';
import index from 'instantsearch.js/es/widgets/index/index';
import type { IndexWidget, Widget } from 'instantsearch.js';
import { Subscription } from 'rxjs';
import { DbService } from '../../../services/db.service';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from '@angular/fire/functions';
import { getApp } from '@angular/fire/app';
import { addIcons } from 'ionicons';
import {
  informationCircleOutline,
  searchOutline,
  returnDownForwardOutline,
  chevronUp,
  chevronDown,
  swapVertical,
  banOutline,
  checkmarkCircleOutline,
  timeOutline,
  closeCircleOutline,
  peopleOutline,
  personCircleOutline,
} from 'ionicons/icons';

type SortField = 'companyName' | 'invitedAt' | 'share' | 'totalBalance' | null;
type SortDirection = 'asc' | 'desc';
type StatusFilter = '' | 'ACTIVE' | 'INVITED' | 'NOT_INVITED' | 'BLOCKED';

interface StatusInfo {
  label: string;
  color: 'dark' | 'success' | 'warning' | 'danger';
  icon: string;
}

@Component({
  selector: 'app-payees-list',
  templateUrl: './payees-list.component.html',
  styleUrls: ['./payees-list.component.scss'],
  imports: [
    CommonModule,
    DatePipe,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonIcon,
    IonCheckbox,
    IonSpinner,
    IonList,
    IonItem,
    IonLabel,
    IonChip,
    IonSelect,
    IonSelectOption,
    IonPopover,
  ],
  standalone: true,
})
export class PayeesListComponent implements OnInit, OnDestroy {
  modalController = inject(ModalController);
  auth = inject(AuthService);
  payeeService = inject(PayeeService);
  instantSearchService = inject(InstantSearchService);
  db = inject(DbService);
  private router = inject(Router);

  readonly advanceType = input<AdvanceType>(undefined);
  readonly type = input<'modal' | 'inline'>('modal');
  readonly isLinkedPayeeSelection = input(false);
  readonly selectedPayee = input<string>(undefined);
  readonly selectedPayees = input<string[]>([]);
  readonly isPaginationPayeesList = viewChild<ElementRef<HTMLElement>>('isPaginationPayeesList');
  readonly isSearchboxPayeesList = viewChild<ElementRef<HTMLElement>>('isSearchboxPayeesList');
  readonly isStatsPayeesList = viewChild<ElementRef<HTMLElement>>('isStatsPayeesList');
  readonly inlineResults = viewChild<ElementRef<HTMLElement>>('inlineResults');
  readonly payeeSelected = output<string>();
  readonly createPayee = output<void>();
  readonly composerSelected = output<{ payeeId: string; composerId?: string }>();

  public searchInitialized = false;
  public hits: BaseHit[] = [];
  public refine: (query: string) => void;
  public query: string;
  private lastQuery: string | null = null;
  private lastPage: number | null = null;
  private currentResultsPage: number | null = null;
  private highlightedPayeeId: string | null = null;
  private pendingScrollToTop = false;
  private payeesIndex: IndexWidget | null = null;
  /** Serial for connectHits async work — skip stale completions when a newer render arrived. */
  private connectHitsUpdateSeq = 0;
  private isDestroyed = false;
  private rawHits: BaseHit[] = [];
  private missingParentsToResolve: Array<{ childId: string; parentId: string }> = [];
  private parentHitCache = new Map<string, any>();
  public loadingChildrenParentIds = new Set<string>();
  signedInPayeeId: string | null = null;
  private signedInPayeeEffectiveId: string | null = null;

  // Sort state
  sortField: SortField = null;
  sortDirection: SortDirection = 'asc';
  private sortByRefine: ((value: string) => void) | null = null;
  private primaryIndexName = environment.search.payeesIndexName;

  // Status filter state
  statusFilter: StatusFilter = '';
  private statusFilterWidget: Widget | null = null;

  // Linked-payee selection mode: server-side filter applied via `configure`
  // so that each Algolia page returns 10 valid candidates (otherwise the
  // legacy client-side filter would decimate each page after the fact).
  private linkedPayeeFilterWidget: Widget | null = null;
  private lastLinkedPayeeFilterKey: string | null = null;

  // Composers popover
  public popoverOpen = false;
  public popoverEvent: Event | null = null;
  public popoverComposers: Array<{ id?: string; companyName?: string; share?: number }> = [];
  private popoverPayeeId: string | null = null;

  advanceValues: number[] = [];
  headerColor = '';
  instantSearchSubscription: Subscription;
  signedInPayeeSubscription: Subscription;
  isCopying = false;
  isDeletingComposers = false;
  private initialPromotionApplied = false;

  constructor() {
    addIcons({
      informationCircleOutline,
      searchOutline,
      returnDownForwardOutline,
      chevronUp,
      chevronDown,
      swapVertical,
      banOutline,
      checkmarkCircleOutline,
      timeOutline,
      closeCircleOutline,
      peopleOutline,
      personCircleOutline,
    });

    // Keep the server-side linked-payee filter in sync with the current
    // selection inputs. Runs whenever any of the related signals change.
    effect(() => {
      const isLinked = this.isLinkedPayeeSelection();
      const parentId = this.selectedPayee();
      const selectedIds = this.selectedPayees() || [];
      this.applyLinkedPayeeFilter(isLinked, parentId, selectedIds);
    });
  }

  ngOnInit() {
    switch (this.advanceType()) {
      case 'advance':
        this.headerColor = 'danger';
        break;
      case 'credit':
        this.headerColor = 'success';
        break;
    }

    this.instantSearchService.initConfig();
    this.initSearchConfig();

    this.signedInPayeeSubscription = this.payeeService.signedInPayee$.subscribe((payee: any) => {
      const previous = this.signedInPayeeId;
      this.signedInPayeeId = payee?.id || null;
      const parentId = payee?.internalProperties?.parentPayee as string | undefined;
      this.signedInPayeeEffectiveId = parentId || this.signedInPayeeId;
      if (this.signedInPayeeId && previous !== this.signedInPayeeId) {
        setTimeout(() => this.ensureSignedInPayeeOnTop());
      }
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.instantSearchSubscription?.unsubscribe();
    this.signedInPayeeSubscription?.unsubscribe();
    if (this.payeesIndex) {
      if (this.statusFilterWidget) {
        try {
          this.payeesIndex.removeWidgets([this.statusFilterWidget]);
        } catch { }
        this.statusFilterWidget = null;
      }
      if (this.linkedPayeeFilterWidget) {
        try {
          this.payeesIndex.removeWidgets([this.linkedPayeeFilterWidget]);
        } catch { }
        this.linkedPayeeFilterWidget = null;
      }
      try {
        this.instantSearchService.removeWidgets([this.payeesIndex]);
      } catch { }
      this.payeesIndex = null;
    }
  }

  initSearchConfig() {
    this.instantSearchSubscription = this.instantSearchService.searchInitialized$.subscribe(initialized => {
      if (initialized) {
        this.searchInitialized = true;
        setTimeout(() => {
          this.connectSearchWidgets();
        });
      }
    });
  }

  refresh() {
    setTimeout(() => {
      this.instantSearchService.refresh();
    }, 100);
  }

  connectSearchWidgets() {
    const searchboxEl = this.isSearchboxPayeesList()?.nativeElement;
    const paginationEl = this.isPaginationPayeesList()?.nativeElement;
    const statsEl = this.isStatsPayeesList()?.nativeElement;
    if (!searchboxEl || !paginationEl || !statsEl) {
      setTimeout(() => this.connectSearchWidgets(), 50);
      return;
    }

    this.payeesIndex = index({ indexName: this.primaryIndexName });
    this.payeesIndex.addWidgets([
      connectSearchBox(({ refine, query }) => {
        const prevQuery = this.query;
        this.refine = refine;
        this.query = query;
        if (prevQuery !== undefined && prevQuery !== query) {
          this.clearHighlight();
        }
        this.lastQuery = query ?? null;
      })({
      }),
      connectHits(({ hits, results }) => {
        void this.onConnectHits(hits, results);
      })({

      }),
      connectPagination(({ currentRefinement }) => {
        if (this.lastPage !== null && this.lastPage !== currentRefinement) {
          this.clearHighlight();
        }
        this.lastPage = currentRefinement;
      })({}),
      connectSortBy(({ refine, currentRefinement }) => {
        this.sortByRefine = refine;
        const { field, direction } = this.parseSortIndex(currentRefinement);
        this.sortField = field;
        this.sortDirection = direction;
      })({
        items: this.buildSortByItems(),
      }),
      searchBox({
        container: searchboxEl,
        placeholder: 'Pesquisar',

      }),
      pagination({
        container: paginationEl,
        totalPages: 30,
        padding: 2,
        showFirst: false,
        showLast: false,
        showPrevious: true,
        showNext: true

      }),
      stats({
        container: statsEl,
        templates: {
          text: (data) => `${data.nbHits} beneficiários encontrados`,
        },
      }),
    ]);
    this.instantSearchService.addWidgets([this.payeesIndex]);

    // The reactive effect may have run before the index existed; re-apply
    // now so the server-side linked-payee filter is registered on first load.
    this.lastLinkedPayeeFilterKey = null;
    this.applyLinkedPayeeFilter(
      this.isLinkedPayeeSelection(),
      this.selectedPayee(),
      this.selectedPayees() || []
    );
  }

  /**
   * Runs after each Algolia hits update. Selected-payee reordering uses DB fetches and must complete
   * before follow-up work so linked selection mode keeps a stable order.
   */
  private async onConnectHits(hits: BaseHit[], results: unknown): Promise<void> {
    const seq = ++this.connectHitsUpdateSeq;
    this.currentResultsPage = (results as any)?.page ?? null;
    this.lastQuery = (results as any)?.query ?? this.lastQuery;
    this.rawHits = hits;
    this.hits = this.expandHitsWithLinkedPayees(this.filterHits(hits));
    this.ensureSignedInPayeeOnTop();
    try {
      await this.ensureSelectedPayeesOnTop();
    } catch {
      // InstantSearch does not await this callback; avoid unhandled rejections.
    }
    if (this.isDestroyed || seq !== this.connectHitsUpdateSeq) {
      return;
    }
    if (this.pendingScrollToTop) {
      this.scrollToTopNow();
      this.pendingScrollToTop = false;
    }
    this.resolveMissingParentsAsync();
  }

  private buildSortByItems(): Array<{ value: string; label: string }> {
    const replicas = environment.search.payeesSortReplicas;
    return [
      { value: this.primaryIndexName, label: 'Padrão' },
      { value: replicas.companyNameAsc, label: 'Nome (A-Z)' },
      { value: replicas.companyNameDesc, label: 'Nome (Z-A)' },
      { value: replicas.invitedAtAsc, label: 'Convidado em (mais antigo)' },
      { value: replicas.invitedAtDesc, label: 'Convidado em (mais recente)' },
      { value: replicas.shareAsc, label: 'Repasse (menor)' },
      { value: replicas.shareDesc, label: 'Repasse (maior)' },
      { value: replicas.totalBalanceAsc, label: 'Total disponível (menor)' },
      { value: replicas.totalBalanceDesc, label: 'Total disponível (maior)' },
    ];
  }

  private parseSortIndex(indexName: string): { field: SortField; direction: SortDirection } {
    const replicas = environment.search.payeesSortReplicas;
    const map: Record<string, { field: SortField; direction: SortDirection }> = {
      [replicas.companyNameAsc]: { field: 'companyName', direction: 'asc' },
      [replicas.companyNameDesc]: { field: 'companyName', direction: 'desc' },
      [replicas.invitedAtAsc]: { field: 'invitedAt', direction: 'asc' },
      [replicas.invitedAtDesc]: { field: 'invitedAt', direction: 'desc' },
      [replicas.shareAsc]: { field: 'share', direction: 'asc' },
      [replicas.shareDesc]: { field: 'share', direction: 'desc' },
      [replicas.totalBalanceAsc]: { field: 'totalBalance', direction: 'asc' },
      [replicas.totalBalanceDesc]: { field: 'totalBalance', direction: 'desc' },
    };
    return map[indexName] || { field: null, direction: 'asc' };
  }

  /**
   * Click handler for a sortable column header. Cycles asc -> desc -> default.
   */
  onSortColumn(field: Exclude<SortField, null>) {
    if (!this.sortByRefine) return;
    let nextIndex: string;
    if (this.sortField !== field) {
      nextIndex = this.getReplicaFor(field, 'asc');
      this.sortField = field;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      nextIndex = this.getReplicaFor(field, 'desc');
      this.sortDirection = 'desc';
    } else {
      nextIndex = this.primaryIndexName;
      this.sortField = null;
      this.sortDirection = 'asc';
    }
    this.clearHighlight();
    try { this.sortByRefine(nextIndex); } catch { }
  }

  private getReplicaFor(field: Exclude<SortField, null>, direction: SortDirection): string {
    const replicas = environment.search.payeesSortReplicas;
    switch (field) {
      case 'companyName':
        return direction === 'asc' ? replicas.companyNameAsc : replicas.companyNameDesc;
      case 'invitedAt':
        return direction === 'asc' ? replicas.invitedAtAsc : replicas.invitedAtDesc;
      case 'share':
        return direction === 'asc' ? replicas.shareAsc : replicas.shareDesc;
      case 'totalBalance':
        return direction === 'asc' ? replicas.totalBalanceAsc : replicas.totalBalanceDesc;
    }
  }

  getSortIconFor(field: Exclude<SortField, null>): 'chevron-up' | 'chevron-down' | 'swap-vertical' {
    if (this.sortField !== field) return 'swap-vertical';
    return this.sortDirection === 'asc' ? 'chevron-up' : 'chevron-down';
  }

  /**
   * Apply / remove the bpaStatus filter based on the selected dropdown value.
   */
  onStatusFilterChange(value: StatusFilter) {
    if (!this.payeesIndex) return;
    this.statusFilter = value;
    this.clearHighlight();

    if (this.statusFilterWidget) {
      try { this.payeesIndex.removeWidgets([this.statusFilterWidget]); } catch { }
      this.statusFilterWidget = null;
    }
    if (value) {
      this.statusFilterWidget = configure({ filters: `internalProperties.bpaStatus:${value}` });
      this.payeesIndex.addWidgets([this.statusFilterWidget]);
    }
    this.instantSearchService.refresh();
  }

  /**
   * Build a chip descriptor for the Status column.
   */
  getStatusInfo(payee: any): StatusInfo {
    const ip = payee?.internalProperties || {};
    if (ip.blocked) {
      return { label: 'Bloqueado', color: 'dark', icon: 'ban-outline' };
    }
    if (ip.lastSignIn) {
      return { label: 'Ativo', color: 'success', icon: 'checkmark-circle-outline' };
    }
    if (ip.invitedAt) {
      return { label: 'Convidado', color: 'warning', icon: 'time-outline' };
    }
    return { label: 'Não convidado', color: 'danger', icon: 'close-circle-outline' };
  }

  /**
   * Normalizes a Firestore Timestamp / number to a Date for the pipe.
   */
  toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value?.toDate === 'function') {
      try { return value.toDate(); } catch { return null; }
    }
    const seconds = value?._seconds ?? value?.seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
    return null;
  }

  /**
   * Show composers popover anchored on click target.
   */
  openComposersPopover(event: Event, payee: any) {
    event.stopPropagation();
    const composers = Array.isArray(payee?.internalProperties?.composers)
      ? payee.internalProperties.composers
      : [];
    if (composers.length === 0) return;
    this.popoverComposers = composers;
    this.popoverPayeeId = payee?.objectID || payee?.id || null;
    this.popoverEvent = event;
    this.popoverOpen = true;
  }

  onComposersPopoverDismiss() {
    this.popoverOpen = false;
    this.popoverEvent = null;
    this.popoverComposers = [];
    this.popoverPayeeId = null;
  }

  /**
   * Clicking a composer inside the popover opens the containing payee's
   * info modal with the "Compositores" tab preselected. Composers are nested
   * entries on a payee (not independent docs), so we always open the owning
   * payee's modal.
   */
  onComposerClick(composer: { id?: string }) {
    this.popoverOpen = false;
    const payeeId = this.popoverPayeeId;
    if (!payeeId) return;
    if (this.type() === 'inline') {
      this.composerSelected.emit({ payeeId, composerId: composer?.id });
    } else {
      this.modalController.dismiss({ payeeId, composerId: composer?.id, openComposersTab: true });
    }
  }

  /**
   * True if this parent row is still waiting for its linked payees to load.
   */
  isLoadingChildren(payee: any): boolean {
    const id = payee?.objectID || payee?.id;
    if (!id) return false;
    return this.loadingChildrenParentIds.has(id);
  }

  filterHits(hits: BaseHit[]): BaseHit[] {
    // In linked-payee selection mode the eligibility filter runs server-side
    // via the `configure` widget (see `applyLinkedPayeeFilter`), so Algolia
    // already returns 10 valid candidates per page. We also skip parent
    // substitution here: hits are leaves (no `parentPayee`) or explicitly
    // selected children of the current parent — both should render as-is.
    if (this.isLinkedPayeeSelection()) {
      return hits;
    }
    return this.substituteParentsAndDeduplicate(hits);
  }

  /**
   * Apply (or remove) the server-side filter used in linked-payee selection
   * mode. The filter keeps each Algolia page at its full size by pre-excluding
   * invited payees, payees that already have children, and the parent being
   * edited — while still allowing currently-selected linked payees through so
   * they can be pinned at the top by `ensureSelectedPayeesOnTop`.
   */
  private applyLinkedPayeeFilter(
    isLinkedSelection: boolean,
    parentBeingEditedId: string | undefined,
    selectedIds: string[]
  ): void {
    if (!this.payeesIndex) return;

    const safeSelected = (selectedIds || []).filter(Boolean);
    const key = isLinkedSelection
      ? `linked|${parentBeingEditedId || ''}|${[...safeSelected].sort().join(',')}`
      : 'off';
    if (key === this.lastLinkedPayeeFilterKey) return;
    this.lastLinkedPayeeFilterKey = key;

    if (this.linkedPayeeFilterWidget) {
      try { this.payeesIndex.removeWidgets([this.linkedPayeeFilterWidget]); } catch { }
      this.linkedPayeeFilterWidget = null;
    }

    if (!isLinkedSelection) {
      try { this.instantSearchService.refresh(); } catch { }
      return;
    }

    const eligibleClause = 'internalProperties.isEligibleAsLinkedPayee:true';
    const allowClauses: string[] = [eligibleClause];
    for (const id of safeSelected) {
      allowClauses.push(`objectID:"${this.escapeForAlgoliaFilter(id)}"`);
    }
    let filters = `(${allowClauses.join(' OR ')})`;
    if (parentBeingEditedId) {
      filters += ` AND NOT objectID:"${this.escapeForAlgoliaFilter(parentBeingEditedId)}"`;
    }

    this.linkedPayeeFilterWidget = configure({ filters });
    this.payeesIndex.addWidgets([this.linkedPayeeFilterWidget]);
    try { this.instantSearchService.refresh(); } catch { }
  }

  private escapeForAlgoliaFilter(value: string): string {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  expandHitsWithLinkedPayees(hits: BaseHit[]): BaseHit[] {
    const expanded: BaseHit[] = [];
    for (const hit of hits as any[]) {
      expanded.push(hit);

      const linkedPayeeIds = hit?.internalProperties?.linkedPayees || [];
      if (linkedPayeeIds.length > 0) {
        const parentId = hit.objectID || hit.id;
        if (parentId) {
          this.loadingChildrenParentIds.add(parentId);
        }
        this.fetchAndInsertLinkedPayees(parentId, linkedPayeeIds, expanded);
      }
    }
    return expanded;
  }

  private fetchAndInsertLinkedPayees(parentId: string, linkedPayeeIds: string[], targetArray: BaseHit[]) {
    this.db.getPayees(linkedPayeeIds).subscribe((linkedPayees: any[]) => {
      const parentIndex = this.hits.findIndex((h: any) => (h.objectID || h.id) === parentId);
      if (parentIndex !== -1) {
        let i = parentIndex + 1;
        while (i < this.hits.length && (this.hits[i] as any).__isChildPayeeDisplay) {
          this.hits.splice(i, 1);
        }

        const rawHitsMap = new Map<string, any>();
        for (const rawHit of this.rawHits as any[]) {
          const id = rawHit?.objectID || rawHit?.id;
          if (id) {
            rawHitsMap.set(id, rawHit);
          }
        }

        const childHits = linkedPayees.map((lp: any) => {
          const childId = lp.id;
          const rawHit = rawHitsMap.get(childId);
          const highlightResult = rawHit?._highlightResult;

          return {
            ...lp,
            objectID: lp.id,
            __isChildPayeeDisplay: true,
            __parentPayeeId: parentId,
            __parentCompanyName: (this.hits[parentIndex] as any)?.companyName,
            ...(highlightResult ? { _highlightResult: highlightResult } : {}),
            internalProperties: {
              ...lp.internalProperties,
              parentPayee: parentId
            }
          };
        });
        this.hits.splice(parentIndex + 1, 0, ...childHits);
      }
      this.loadingChildrenParentIds.delete(parentId);
    }, () => {
      this.loadingChildrenParentIds.delete(parentId);
    });
  }

  private normalizeHitId(hit: any): string | null {
    return (hit && (hit.objectID || hit.id)) || null;
  }

  private substituteParentsAndDeduplicate(hits: BaseHit[]): BaseHit[] {
    const idToHit = new Map<string, any>();
    for (const h of hits as any[]) {
      const id = this.normalizeHitId(h);
      if (id) { idToHit.set(id, h); }
    }

    const result: any[] = [];
    const seenIndex = new Map<string, number>();

    for (const rawHit of hits as any[]) {
      const childId = this.normalizeHitId(rawHit);
      const parentId = rawHit?.internalProperties?.parentPayee as string | undefined;

      let candidate: any = rawHit;
      let matchedChildId: string | null = null;
      let matchedChildHighlight: string | undefined;
      if (parentId) {
        const parentFromHits = idToHit.get(parentId);
        const parentFromCache = this.parentHitCache.get(parentId);
        if (parentFromHits) {
          candidate = { ...parentFromHits };
          matchedChildId = childId;
          matchedChildHighlight = rawHit?._highlightResult?.companyName?.value || rawHit?.companyName;
        } else if (parentFromCache) {
          candidate = { ...parentFromCache };
          matchedChildId = childId;
          matchedChildHighlight = rawHit?._highlightResult?.companyName?.value || rawHit?.companyName;
        } else if (childId) {
          this.enqueueMissingParent(childId, parentId);
        }
      }

      const candidateId = this.normalizeHitId(candidate);
      if (candidateId && !seenIndex.has(candidateId)) {
        if (matchedChildId) {
          candidate = { ...candidate, __matchedLinkedPayeeId: matchedChildId, __matchedLinkedPayeeNameHighlight: matchedChildHighlight };
        }
        result.push(candidate);
        seenIndex.set(candidateId, result.length - 1);
      } else if (candidateId && matchedChildId) {
        const idx = seenIndex.get(candidateId)!;
        const existing = result[idx];
        result[idx] = { ...existing, __matchedLinkedPayeeId: matchedChildId, __matchedLinkedPayeeNameHighlight: matchedChildHighlight };
      }
    }

    return result;
  }

  private enqueueMissingParent(childId: string, parentId: string): void {
    const exists = this.missingParentsToResolve.some(x => x.parentId === parentId);
    if (!exists) {
      this.missingParentsToResolve.push({ childId, parentId });
    }
  }

  private async resolveMissingParentsAsync(): Promise<void> {
    if (!this.missingParentsToResolve.length) { return; }

    const parentsToFetch = Array.from(new Set(this.missingParentsToResolve
      .map(x => x.parentId)
      .filter(pid => !!pid && !this.parentHitCache.has(pid))));

    this.missingParentsToResolve = [];

    for (const parentId of parentsToFetch) {
      try {
        const parent: any = await this.db.getPayeeOnceWithId(parentId);
        if (parent) {
          const parentHit: any = { ...parent, objectID: parent.id };
          this.parentHitCache.set(parentId, parentHit);
        }
      } catch {
        // ignore fetch errors
      }
    }

    const refreshed = this.filterHits(this.rawHits);
    this.hits = this.expandHitsWithLinkedPayees(refreshed);
    await this.ensureSignedInPayeeOnTop();
    await this.ensureSelectedPayeesOnTop();
  }

  trackById(index: number, item: any): string {
    return item?.objectID;
  }

  public search(event: Event) {
    this.refine!((event.target as HTMLInputElement).value);
  }

  public setHighlightedPayee(id?: string | null) {
    this.highlightedPayeeId = id || null;
    this.pendingScrollToTop = true;
    this.applyHighlightAndReorder();
  }

  private clearPromotionFlags(except?: string) {
    if (!Array.isArray(this.hits)) return;
    for (const h of this.hits as any[]) {
      const hid = h?.objectID || h?.id;
      if (hid !== except) {
        try { delete h.__selected; } catch { h.__selected = false; }
        try { delete h.__highlight; } catch { h.__highlight = false; }
      }
    }
  }

  private async applyHighlightAndReorder() {
    if (!this.highlightedPayeeId) { return; }
    if (!Array.isArray(this.hits)) { this.hits = []; }
    const idx = this.hits.findIndex(h => (h as any).objectID === this.highlightedPayeeId || (h as any).id === this.highlightedPayeeId);
    try {
      const highlighted = await this.db.getPayeeOnceWithId(this.highlightedPayeeId);
      let effectiveId = this.highlightedPayeeId;
      if (highlighted) {
        const parentId = (highlighted as any)?.internalProperties?.parentPayee as string | undefined;
        if (parentId) { effectiveId = parentId; }
      }

      const effective = await this.db.getPayeeOnceWithId(effectiveId);
      if (effective) {
        const fresh: any = { ...(effective as any), objectID: (effective as any).id, __highlight: true };
        if (idx > -1) { this.hits.splice(idx, 1); }
        const dupIdx = this.hits.findIndex(h => (h as any).objectID === fresh.objectID || (h as any).id === fresh.id);
        if (dupIdx > -1) { this.hits.splice(dupIdx, 1); }
        this.hits.unshift(fresh);
        this.clearPromotionFlags(fresh.objectID || fresh.id);

        const parentIdForChildren = fresh.objectID || fresh.id;
        const linkedIds: string[] = fresh?.internalProperties?.linkedPayees || [];
        if (parentIdForChildren && linkedIds.length > 0) {
          this.hits = this.hits.filter((h: any) => !(h?.__isChildPayeeDisplay && h?.__parentPayeeId === parentIdForChildren));
          this.loadingChildrenParentIds.add(parentIdForChildren);
          this.fetchAndInsertLinkedPayees(parentIdForChildren, linkedIds, this.hits);
        }
      } else if (idx > -1) {
        const [item] = this.hits.splice(idx, 1);
        (item as any).__highlight = true;
        this.hits.unshift(item);
        this.clearPromotionFlags((item as any)?.objectID || (item as any)?.id);
      }
    } catch {
      if (idx > -1) {
        const [item] = this.hits.splice(idx, 1);
        (item as any).__highlight = true;
        this.hits.unshift(item);
        this.clearPromotionFlags((item as any)?.objectID || (item as any)?.id);
      }
    }
  }

  private async ensureSelectedPayeesOnTop(): Promise<void> {
    const isLinkedSelection = this.isLinkedPayeeSelection?.() === true;
    const selectedIds = (this.selectedPayees?.() || []).filter(Boolean);
    const hasActiveSearch = (this.lastQuery ?? '').trim().length > 0;
    let isFirstPage = false;
    if (this.currentResultsPage !== null) {
      isFirstPage = this.currentResultsPage === 0;
    } else if (this.lastPage !== null) {
      isFirstPage = this.lastPage === 0 || this.lastPage === 1;
    } else {
      isFirstPage = true;
    }
    if (!isLinkedSelection || !selectedIds.length || !isFirstPage || hasActiveSearch) {
      await this.applyHighlightAndReorder();
      return;
    }

    const filteredHits = Array.isArray(this.hits) ? [...this.hits] : [];
    const selectedIdSet = new Set<string>(selectedIds);
    const existingSelectedMap = new Map<string, any>();
    const nonSelected: any[] = [];

    for (const hit of filteredHits) {
      const id = (hit as any).objectID || (hit as any).id;
      if (id && selectedIdSet.has(id)) {
        existingSelectedMap.set(id, hit);
      } else {
        nonSelected.push(hit);
      }
    }

    const missingIds = selectedIds.filter(id => !existingSelectedMap.has(id));
    const fetchedSelected: any[] = [];
    for (const id of missingIds) {
      try {
        const payee = await this.db.getPayeeOnceWithId(id);
        if (payee) {
          const fresh: any = { ...(payee as any), objectID: (payee as any).id };
          fetchedSelected.push(fresh);
        }
      } catch {
        // Ignore fetch errors
      }
    }

    const selectedBlock: any[] = [];
    for (const id of selectedIds) {
      const fromExisting = existingSelectedMap.get(id);
      const fromFetched = fetchedSelected.find(x => (x as any).objectID === id || (x as any).id === id);
      if (fromExisting) {
        selectedBlock.push(fromExisting);
      } else if (fromFetched) {
        selectedBlock.push(fromFetched);
      }
    }

    this.hits = [...selectedBlock, ...nonSelected];
    await this.applyHighlightAndReorder();
  }

  private clearHighlight() {
    this.highlightedPayeeId = null;
  }

  private scrollToTopNow() {
    const container = this.inlineResults()?.nativeElement || document.querySelector('app-payees-list .content-section');
    try { (container as any)?.scrollTo?.({ top: 0, behavior: 'smooth' }); } catch { }
    try { (container as any).scrollTop = 0; } catch { }
    const ionContent = document.querySelector('ion-content') as any;
    try { ionContent?.scrollToTop?.(300); } catch { }
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { }
  }

  select(payeeId?: string) {
    if (this.type() === 'inline') {
      this.payeeSelected.emit(payeeId);
    } else {
      this.modalController.dismiss(payeeId);
    }
  }

  selectAsCurrentPayee(payee: any) {
    const id = payee?.objectID || payee?.id;
    if (!id) return;
    this.payeeService.selectPayeeId(id);
    this.router.navigate(['/pages/earnings']);
  }

  public highlightAndScrollTop(id: string) {
    this.setHighlightedPayee(id);
    requestAnimationFrame(() => this.scrollToTopNow());
    setTimeout(() => this.scrollToTopNow(), 200);
  }

  async copyArtistToPayee() {
    this.isCopying = true;
    const functions = getFunctions(getApp(), environment.defaultRegion);
    if (environment.useFunctionsEmulator) {
      connectFunctionsEmulator(functions, 'localhost', 6001);
    }
    const copyAllArtistsToRevelatorPayees = httpsCallable(functions, 'copyAllArtistsToRevelatorPayees');
    await copyAllArtistsToRevelatorPayees();
    this.isCopying = false;
    this.refresh();
  }

  async deleteAllComposers() {
    this.isDeletingComposers = true;
    await this.db.deleteAllComposers();
    this.isDeletingComposers = false;
    this.refresh();
  }

  private ensureSignedInPayeeOnTop(): void {
    if (this.isLinkedPayeeSelection?.() === true) {
      this.applyHighlightAndReorder();
      return;
    }

    let hasActiveSearch = (this.lastQuery ?? '').trim().length > 0;
    let isFirstPage = false;
    if (this.currentResultsPage !== null) {
      isFirstPage = this.currentResultsPage === 0;
    } else if (this.lastPage !== null) {
      isFirstPage = this.lastPage === 0 || this.lastPage === 1;
    } else {
      isFirstPage = true;
    }
    if (!this.initialPromotionApplied) {
      hasActiveSearch = false;
      isFirstPage = true;
    }

    if (!this.signedInPayeeId || hasActiveSearch || !isFirstPage) {
      this.applyHighlightAndReorder();
      return;
    }

    if (!Array.isArray(this.hits)) { this.hits = []; }

    const effectiveId = this.signedInPayeeEffectiveId || this.signedInPayeeId;
    const shouldMarkSelected = effectiveId === this.signedInPayeeId;

    this.clearPromotionFlags(effectiveId);

    const currentIndex = this.hits.findIndex((h: any) => (h?.objectID || h?.id) === effectiveId);
    if (currentIndex === -1) {
      // Parent not in current hits - fetch asynchronously and insert at top.
      // This path is rare (e.g. first load before Algolia returns anything containing the payee)
      // and is kept async because we need the payee record from the DB.
      this.promoteSignedInPayeeFromDb(effectiveId, shouldMarkSelected);
    } else {
      // Parent is already in hits. Move the [parent + its existing children] block together
      // to the top without removing/re-fetching children, to avoid visible re-renders.
      const parentIdForChildren = (this.hits[currentIndex] as any)?.objectID || (this.hits[currentIndex] as any)?.id;
      let endIndex = currentIndex + 1;
      while (
        endIndex < this.hits.length &&
        (this.hits[endIndex] as any)?.__isChildPayeeDisplay &&
        (this.hits[endIndex] as any)?.__parentPayeeId === parentIdForChildren
      ) {
        endIndex++;
      }
      const block = this.hits.splice(currentIndex, endIndex - currentIndex);
      const item: any = block[0];
      if (shouldMarkSelected) {
        item.__selected = true;
      } else {
        try { delete item.__selected; } catch { item.__selected = false; }
      }
      this.hits.unshift(...block);

      // If the parent has linked payees that haven't all been loaded yet, let the
      // still-pending fetchAndInsertLinkedPayees finish (it will splice children
      // at the parent's new position). Only start a new fetch if none is in flight.
      const linkedIds: string[] = item?.internalProperties?.linkedPayees || [];
      const loadedChildrenCount = block.length - 1;
      if (
        parentIdForChildren &&
        linkedIds.length > 0 &&
        loadedChildrenCount < linkedIds.length &&
        !this.loadingChildrenParentIds.has(parentIdForChildren)
      ) {
        this.loadingChildrenParentIds.add(parentIdForChildren);
        this.fetchAndInsertLinkedPayees(parentIdForChildren, linkedIds, this.hits);
      }
    }

    this.initialPromotionApplied = true;
    this.applyHighlightAndReorder();
  }

  private async promoteSignedInPayeeFromDb(effectiveId: string, shouldMarkSelected: boolean): Promise<void> {
    try {
      const payee = await this.db.getPayeeOnceWithId(effectiveId);
      if (!payee) return;
      const fresh: any = {
        ...(payee as any),
        objectID: (payee as any).id,
        ...(shouldMarkSelected ? { __selected: true } : {})
      };
      const dupIdx = this.hits.findIndex(h => (h as any).objectID === fresh.objectID || (h as any).id === fresh.id);
      if (dupIdx > -1) { this.hits.splice(dupIdx, 1); }
      this.hits.unshift(fresh);

      const parentIdForChildren = fresh.objectID || fresh.id;
      const linkedIds: string[] = fresh?.internalProperties?.linkedPayees || [];
      if (parentIdForChildren && linkedIds.length > 0) {
        this.hits = this.hits.filter((h: any) => !(h?.__isChildPayeeDisplay && h?.__parentPayeeId === parentIdForChildren));
        this.loadingChildrenParentIds.add(parentIdForChildren);
        this.fetchAndInsertLinkedPayees(parentIdForChildren, linkedIds, this.hits);
      }
    } catch {
      // Ignore fetch errors
    }
  }

}
